import { WorldState, groupByPos, WorldHandler } from "./world";
import {
  CharacterState,
  stripStatus as stripStatusFromCharacter,
  StatusType,
} from "./characters";
import { ResourceNodeState, ResourceType } from "./resources";
import { DmgStatus, StatusHandler as SH } from "./status";
import { Dictionary } from "../utils/dictionaries";
import { FactionType, SocialHandler } from "./social";

export type HungryStatus = DmgStatus<StatusType.STARVING>;

const hungerEffect = (c: CharacterState): CharacterState => {
  const hungryStatus = c.statusList.find((s) => s.type === StatusType.STARVING);

  const duration = hungryStatus ? hungryStatus.duration : 0;
  const dmg = Math.round((duration + 1) * Math.random());

  const newHungerStatus: HungryStatus = SH.createStatus(StatusType.STARVING, {
    dmg,
  }) as HungryStatus;

  return {
    ...c,
    statusList: SH.addDmgStatus(newHungerStatus, c.statusList),
  };
};

export const doEat2 = (w: WorldState): WorldState => {
  const groupedResources: Dictionary<ResourceNodeState[]> = w.resources
    .filter((r) => r.resource === ResourceType.FOOD)
    .reduce(groupByPos<ResourceNodeState>(), {});

  const posDict = WorldHandler.getPosDictionary(w, "id");
  const { agentGroups: charactersPerPos2, meta: charactersPosIndex2 } =
    SocialHandler.SplitByFaction(
      FactionType.POS,
      w.characters,
      w.factions.filter((f) => f.type === FactionType.POS)
    );

  //only foodNodes with characters within it
  const foodPerPos: [string, number][] = charactersPosIndex2.map((e) => {
    const pos = posDict[e.key].name;
    const foodNodes = groupedResources[pos];
    return [pos, foodNodes && foodNodes.length > 0 ? foodNodes[0].quantity : 0];
  });

  //compute what is available => foodPerPos
  //resolve characters
  const stripHungerFromChar = stripStatusFromCharacter(StatusType.STARVING);
  const mapCharacterAtPos =
    (foodAvailable: number) =>
    (char: CharacterState, i: number): CharacterState => {
      return i < foodAvailable ? stripHungerFromChar(char) : hungerEffect(char);
    };

  const sortGroupByPosInfluence = (
    charGroup: CharacterState[]
  ): CharacterState[] => {
    const sortByInfluenceDesc = SocialHandler.sortByFaction(
      FactionType.POS,
      "influence",
      true
    );
    return charGroup.sort(sortByInfluenceDesc);
  };

  //console.time("eatProcessChars");
  //@TODO: agregate family influence on this position
  //ORDER and GROUP character by families (most influent families first / and families character order by influence)
  //ADULT SHOULD EAT MORE THAN CHILDREN
  //OR ALTERNATIVE => Food is collected by influence order only and stored instead consumed
  //in a next turn phase, food stock from character can be split with family at same pos
  const newCharacters2 = charactersPerPos2
    .map(sortGroupByPosInfluence)
    .flatMap((e) => {
      const { pos } = e[0];
      const foodEntry = foodPerPos.find(
        (fpp) => fpp[0] === `${WorldHandler.posStr(pos)}`
      );
      const foodAvailable: number = foodEntry ? foodEntry[1] : 0;

      return e.map(mapCharacterAtPos(foodAvailable));
    });
  //console.timeEnd("eatProcessChars");

  //resolve resources
  const newResources2: ResourceNodeState[] = w.resources.map((r) => {
    if (r.resource !== ResourceType.FOOD) {
      return r;
    }

    const charactersAtPos = charactersPosIndex2.find(
      (cpi) => posDict[cpi.key].name === WorldHandler.posStr(r.pos)
    );
    if (!charactersAtPos) {
      return r;
    }

    return {
      ...r,
      quantity: r.quantity - Math.min(charactersAtPos.quantity, r.quantity),
    };
  });

  return {
    ...w,
    characters: newCharacters2,
    resources: newResources2,
  };
};
